import { AdminClient } from 'graphql/clients'
import {
  DoNotContactReason,
  DynamicallyFilteredAccountsDocument,
  DynamicallyFilteredAccountsQuery,
  ParentOrChildRelationshipFragment,
} from 'graphql/bindings/AdminBindings'
import { GridOptions, IServerSideDatasource, IServerSideGetRowsParams } from 'ag-grid-community'
import { Routes } from 'util/Routes'
import { getCode, getName } from 'country-list'
import invert from 'lodash/invert'
import moment from 'moment'
import uniq from 'lodash/uniq'

const structureConverter: { [key: string]: string } = {
  forward_backed_fund_interest: 'Forwards',
  share_backed_fund_interest: 'SPVs',
  direct_share_transfer: 'Directs',
}

const boolFields = [
  'has_transacted',
  'has_done_primaries',
  'has_done_secondaries',
  'only_profitable_companies',
  'has_cash_on_hand',
]

export const doNotContactOptions: { [key in DoNotContactReason]: string } = {
  [DoNotContactReason.IsUnsubscribedFromDealRelatedEmails]: 'DNC: Unsubscribed',
  [DoNotContactReason.HasIntermediary]: 'DNC: Via intermediary',
  [DoNotContactReason.IsHighSensitivity]: 'DNC: Via Forge owner',
  [DoNotContactReason.IsInRestrictedLocation]: 'DNC: Country restricted',
  [DoNotContactReason.IsForgeDeclined]: 'DNC: Forge declined',
}

const doNotContactConvertToValue = (input: string): DoNotContactReason | null => {
  const invertedOptions = invert(doNotContactOptions)

  return invertedOptions[input] as DoNotContactReason | null
}

const doNotContactConvertToDisplay = (input: DoNotContactReason): string => {
  if (input) {
    return doNotContactOptions[input]
  }
  return 'OK to contact'
}

const getIntermediaryContactInfo = (intermediary: ParentOrChildRelationshipFragment | undefined, accountId: string) => {
  const isParent = (intermediary?.parent_account.id ?? '') === accountId
  const data = {
    name: isParent ? intermediary?.child_account?.name ?? '' : intermediary?.parent_account?.name ?? '',
    email: isParent ? intermediary?.child_account?.email ?? '' : intermediary?.parent_account?.email ?? '',
    role: intermediary?.title ?? '',
  }
  return data
}

class AccountsDatasource implements IServerSideDatasource {
  gridOptions: GridOptions
  client = AdminClient

  constructor(gridOptions: GridOptions) {
    this.gridOptions = gridOptions
  }

  getRows(params: IServerSideGetRowsParams): void {
    const filterModel = params.request.filterModel
    const filtersAnd = Object.entries(filterModel).map(([key, value]) => {
      const name = key.endsWith('_1') ? key.slice(0, key.length - 2) : key
      const data = value as any
      const filterType: string = data.filterType as string
      switch (filterType) {
        case 'set':
          if (boolFields.includes(name)) {
            return {
              name: name,
              operation: '=',
              value: data.values[0] === 'true',
            }
          }
          if (name === 'investment_structure_comfort') {
            return {
              name: name,
              operation: 'contains',
              value: data.values,
            }
          }
          if (name === 'investor_level') {
            return {
              name: 'account_level',
              operation: 'in',
              value: data.values,
            }
          }
          if (name === 'country') {
            return {
              name: name,
              operation: 'contains',
              value: data.values.map((country: string | null) => (country ? getCode(country) : country)),
            }
          }
          if (name === 'investor_shareholder') {
            const filts =
              data.values.length > 0
                ? [
                    data.values.includes('Investor') && {
                      name: 'is_investor',
                      operation: '=',
                      value: true,
                    },
                    data.values.includes('Shareholder') && {
                      name: 'is_shareholder',
                      operation: '=',
                      value: true,
                    },
                  ].filter(s => !!s)
                : [
                    {
                      name: 'is_investor',
                      operation: '=',
                      value: false,
                    },
                    {
                      name: 'is_shareholder',
                      operation: '=',
                      value: false,
                    },
                  ]
            return {
              filters: filts,
              logicOp: 'and',
            }
          }
          if (name === 'do_not_contact_reasons') {
            return {
              name: 'do_not_contact_reasons',
              operation: 'in',
              value: data.values.map(doNotContactConvertToValue),
            }
          }
          return {
            name: name,
            operation: 'in',
            value: data.values,
          }
        case 'number':
          return {
            name: name,
            operation: '>',
            value: data.filter,
          }
        case 'date':
          return {
            name: name,
            operation: '>',
            value: moment(data.dateFrom).toISOString(),
          }
        default:
          return {
            name: name,
            operation: 'contains',
            value: data.filter,
          }
      }
    })
    const topLevelFilter = {
      filters: filtersAnd,
      logicOp: 'and',
    }
    const offset = params.request.startRow
    const limit = params.request.endRow - offset
    this.client
      .query<DynamicallyFilteredAccountsQuery>({
        query: DynamicallyFilteredAccountsDocument,
        variables: {
          filter: JSON.stringify(topLevelFilter),
          offset,
          limit: limit,
        },
        fetchPolicy: 'network-only',
      })
      .then(response => {
        const rows: any = response.data.dynamicQuery.data.map(row => {
          const investor_preferences = row?.investor_preferences
          const intermediaryInfo = getIntermediaryContactInfo(row?.intermediary ?? undefined, row.id)
          const relationships = row?.relationships ?? []
          const points_of_contact = relationships
            .filter(rel => rel.is_point_of_contact)
            .map(rel => `${rel.child_account.name} <${rel.child_account.email}>`)
          return {
            id: row.id,
            name: row.name,
            investor_shareholder: [row.is_investor && 'Investor', row.is_shareholder && 'Shareholder'].filter(s => !!s),
            owners: row.owners.map(owner => `${owner.first_name} ${owner.last_name.charAt(0).toUpperCase()}`),
            type: row.type,
            client_type: row.client_type,
            email: row.email,
            phone: row.formatted_phone_number,
            has_transacted: row.avg_order_size > 0,
            has_done_primaries: row.has_done_primaries_before,
            has_done_secondaries: row.participates_in_secondaries,
            net_worth: row.net_worth,
            aum: row.aum,
            region: row.region,
            country: row.derived_location.countryCode && getName(row.derived_location.countryCode),
            state: row.derived_location.state,
            city: row.derived_location.city,
            aum_secondaries: row.aum_secondaries,
            investment_structure_comfort: (investor_preferences?.investment_structure_comfort ?? []).map(
              s => structureConverter[s]
            ),
            minimum_company_valuation: investor_preferences?.minimum_company_valuation ?? '',
            minimum_company_revenue: investor_preferences?.minimum_company_revenue ?? '',
            minimum_growth_rate: investor_preferences?.minimum_growth_rate ?? '',
            only_profitable_companies: investor_preferences?.only_profitable_companies ?? undefined,
            opportunities_of_interest: investor_preferences?.opportunities_of_interest ?? [],
            target_regions: investor_preferences?.target_regions ?? [],
            target_check_size: investor_preferences?.target_check_size ?? '',
            has_cash_on_hand: investor_preferences?.has_cash_on_hand,
            sectors_of_interest: investor_preferences?.sectors_interested ?? [],
            companies_of_interest: row.companies_of_interest.map(comp => comp.name),
            purchased_companies: row.purchased_companies.map(comp => comp.name),
            holdings: uniq(row.holdings.map(holding => holding.company.name)),
            average_order_size: row.avg_order_size,
            last_order_closed_date: row.last_order_closed_date,
            last_order_created_date: row.last_order_created_date,
            num_active_deals: row.num_active_deals,
            num_closed_deals: row.num_closed_deals,
            num_introductions: row.num_introductions,
            order_structures: row.order_structures,
            url: Routes.account(row.id),
            do_not_contact_reasons:
              row.do_not_contact_reasons.length === 0
                ? ['OK to contact']
                : row.do_not_contact_reasons.map(doNotContactConvertToDisplay),
            intermediary_name: intermediaryInfo.name,
            intermediary_email: intermediaryInfo.email,
            intermediary_title: intermediaryInfo.role,
            investor_level: row.account_level,
            business_development_tier: row.business_development_tier,
            points_of_contact: points_of_contact,
          }
        })

        // pass results to grid
        params.successCallback(rows, response.data.dynamicQuery.count)
      })
      .catch(err => {
        console.error(err)
        params.failCallback()
      })
  }
}

export default AccountsDatasource
